/* Fullscreen zoom view for the product gallery images.
   Extends ModalDialog (global.js) for focus trap, scroll lock, Escape, backdrop
   click and the close button (.base-modal__close) — those are bound in the
   ModalDialog constructor, so we only wire the zoom-specific behaviour here.
   We deliberately do NOT add the `media-modal` class (it re-enables the
   close-on-pointerup behaviour that would make thumbnails/arrows unusable). */
if (!customElements.get('product-media-zoom')) {
  class ProductMediaZoom extends ModalDialog {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.stage = this.querySelector('[data-zoom-stage]');
      this.imageEls = Array.from(this.querySelectorAll('.product-media-zoom__image'));
      this.thumbEls = Array.from(this.querySelectorAll('[data-zoom-thumbs] .thumbnail'));
      this.counterEl = this.querySelector('[data-zoom-current]');
      this.totalEl = this.querySelector('[data-zoom-total]');
      this.steps = [1, 2, 3];
      this.stepIndex = 0;
      this.index = 0;
      this.panX = 0;
      this.panY = 0;
      // null = every image is visible (today's behaviour). Set by setVisible()
      // to a variant's ordered group id list.
      this.visibleIds = null;

      // Images render with `hidden` (pre-JS fallback); switch to opacity-based
      // visibility (.is-active) so changing image crossfades.
      this.imageEls.forEach((img) => img.removeAttribute('hidden'));

      this.thumbEls.forEach((btn) => {
        btn.addEventListener('click', () => this.setActive(btn.dataset.mediaId));
      });
      this.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.step(1));

      if (this.stage) {
        this.stage.addEventListener('click', (e) => {
          if (e.target.classList.contains('product-media-zoom__image') && !this._dragged) this.cycleZoom(e);
        });
        this._setupDragPan();
      }

      // Arrow keys / +/- / 0 (Escape + close come from ModalDialog).
      this.addEventListener('keydown', (e) => this._onKeydown(e));

      // If a variant group was applied before this element upgraded, re-apply it.
      if (this._pendingVisibleIds) {
        this.setVisible(this._pendingVisibleIds);
        this._pendingVisibleIds = null;
      }
    }

    get scale() {
      return this.steps[this.stepIndex];
    }

    get activeImage() {
      return this.imageEls[this.index];
    }

    // The ordered list of media ids the zoom navigates. visibleIds when a variant
    // group is applied, else every image in DOM order.
    get orderedIds() {
      return this.visibleIds || this.imageEls.map((img) => String(img.dataset.mediaId));
    }

    // Restrict the zoom to a variant's group (ordered ids). No ids / empty = all.
    setVisible(ids) {
      if (!this.imageEls) {
        // Called before connectedCallback — remember and apply on upgrade.
        this._pendingVisibleIds = ids;
        return;
      }
      this.visibleIds = (ids && ids.length) ? ids.map(String) : null;
      const inSet = (id) => !this.visibleIds || this.visibleIds.indexOf(String(id)) !== -1;

      // Show/hide thumbnails and order the strip to the group order.
      this.thumbEls.forEach((btn) => {
        const li = btn.closest('li');
        if (li) li.hidden = !inSet(btn.dataset.mediaId);
      });
      const thumbsList = this.querySelector('[data-zoom-thumbs]');
      if (this.visibleIds && thumbsList) {
        for (let i = this.visibleIds.length - 1; i >= 0; i--) {
          const id = this.visibleIds[i];
          const btn = this.thumbEls.find((b) => String(b.dataset.mediaId) === id);
          const li = btn && btn.closest('li');
          if (li) thumbsList.prepend(li);
        }
      }

      if (this.totalEl) this.totalEl.textContent = this.orderedIds.length;

      // Keep the active image inside the visible set.
      const current = this.activeImage ? String(this.activeImage.dataset.mediaId) : null;
      if (this.visibleIds && (!current || this.visibleIds.indexOf(current) === -1)) {
        this.setActive(this.visibleIds[0]);
      } else {
        this.setActive(current || this.orderedIds[0]);
      }
    }

    show(opener) {
      super.show(opener);
      const id = opener && opener.dataset ? opener.dataset.mediaId : null;
      this.setActive(id || this.orderedIds[0]);
    }

    hide() {
      this.resetZoom();
      super.hide();
    }

    setActive(id) {
      let idx = this.imageEls.findIndex((img) => img.dataset.mediaId === String(id));
      if (idx < 0) idx = 0;
      this.index = idx;

      this.imageEls.forEach((img, i) => {
        img.removeAttribute('hidden');
        img.classList.toggle('is-active', i === idx);
      });
      this.thumbEls.forEach((btn, i) => {
        if (i === idx) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
      if (this.counterEl) {
        const activeId = String(this.imageEls[idx] && this.imageEls[idx].dataset.mediaId);
        const pos = this.orderedIds.indexOf(activeId);
        this.counterEl.textContent = (pos >= 0 ? pos : 0) + 1;
      }

      this.resetZoom();

      const li = this.thumbEls[idx] && this.thumbEls[idx].closest('li');
      if (li && li.scrollIntoView) li.scrollIntoView({ block: 'nearest', inline: 'center' });
    }

    step(dir) {
      const list = this.orderedIds;
      const n = list.length;
      if (!n) return;
      const curId = this.activeImage ? String(this.activeImage.dataset.mediaId) : list[0];
      let pos = list.indexOf(curId);
      if (pos < 0) pos = 0;
      const next = (pos + dir + n) % n;
      this.setActive(list[next]);
    }

    // Click cycles the zoom 1 → 2 → 3 → 1, focusing on the clicked point.
    cycleZoom(e) {
      this.stepIndex = (this.stepIndex + 1) % this.steps.length;
      if (this.scale === 1) {
        this.resetZoom();
        return;
      }
      const img = this.activeImage;
      if (!img) return;
      const rect = img.getBoundingClientRect();
      const prevScale = rect.width / img.clientWidth || 1;
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      this.panX = -(cx / prevScale);
      this.panY = -(cy / prevScale);
      this._applyTransform();
    }

    _applyTransform() {
      const img = this.activeImage;
      if (!img) return;
      const b = this._panBounds();
      this.panX = Math.max(-b.x, Math.min(b.x, this.panX));
      this.panY = Math.max(-b.y, Math.min(b.y, this.panY));
      img.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`;
      if (this.stage) this.stage.classList.toggle('is-zoomed', this.scale > 1);
    }

    // Max pan (in unscaled px, since translate is applied before scale).
    _panBounds() {
      const img = this.activeImage;
      if (!img || !this.stage) return { x: 0, y: 0 };
      const s = this.scale;
      const stageRect = this.stage.getBoundingClientRect();
      const overflowX = Math.max(0, (img.clientWidth * s - stageRect.width) / 2);
      const overflowY = Math.max(0, (img.clientHeight * s - stageRect.height) / 2);
      return { x: overflowX / s, y: overflowY / s };
    }

    resetZoom() {
      this.stepIndex = 0;
      this.panX = 0;
      this.panY = 0;
      this.imageEls.forEach((img) => {
        img.style.transform = '';
      });
      if (this.stage) {
        this.stage.classList.remove('is-zoomed');
        this.stage.classList.remove('is-panning');
      }
    }

    // Drag to pan while zoomed. Tracks on window (no setPointerCapture, which
    // would retarget the follow-up click away from the image — see PR #163).
    // The pan bounds are read once on pointerdown, not on every move, and the
    // image transition is dropped (via .is-panning) so touch dragging stays
    // smooth instead of easing each frame.
    _setupDragPan() {
      let down = false;
      let sx = 0;
      let sy = 0;
      let spx = 0;
      let spy = 0;
      let moved = 0;
      let bounds = { x: 0, y: 0 };

      const onMove = (e) => {
        if (!down) return;
        const dx = e.clientX - sx;
        const dy = e.clientY - sy;
        moved += Math.abs(dx) + Math.abs(dy);
        let px = spx + dx / this.scale;
        let py = spy + dy / this.scale;
        px = Math.max(-bounds.x, Math.min(bounds.x, px));
        py = Math.max(-bounds.y, Math.min(bounds.y, py));
        this.panX = px;
        this.panY = py;
        const img = this.activeImage;
        if (img) img.style.transform = `translate(${px}px, ${py}px) scale(${this.scale})`;
      };
      const onUp = () => {
        if (!down) return;
        down = false;
        if (this.stage) this.stage.classList.remove('is-panning');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        if (moved > 6) {
          this._dragged = true;
          setTimeout(() => {
            this._dragged = false;
          }, 0);
        }
      };

      this.stage.addEventListener('pointerdown', (e) => {
        if (this.scale === 1) return;
        down = true;
        moved = 0;
        sx = e.clientX;
        sy = e.clientY;
        spx = this.panX;
        spy = this.panY;
        bounds = this._panBounds();
        this.stage.classList.add('is-panning');
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
      });
      this.stage.addEventListener('dragstart', (e) => e.preventDefault());
    }

    _onKeydown(e) {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.step(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.step(1);
          break;
        case '+':
        case '=':
          e.preventDefault();
          this._zoomStep(1);
          break;
        case '-':
        case '_':
          e.preventDefault();
          this._zoomStep(-1);
          break;
        case '0':
          e.preventDefault();
          this.resetZoom();
          break;
        default:
          break;
      }
    }

    _zoomStep(dir) {
      this.stepIndex = Math.max(0, Math.min(this.steps.length - 1, this.stepIndex + dir));
      if (this.scale === 1) {
        this.resetZoom();
        return;
      }
      this._applyTransform();
    }
  }

  customElements.define('product-media-zoom', ProductMediaZoom);
}
